import {
  Badge,
  Button,
  Card,
  CardHeader,
  CodeChip,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  EmptyState,
  Field,
  Input,
  Textarea,
  PageHeader,
  Skeleton,
  Table,
  TableShell,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Tbody,
  Td,
  Th,
  Thead,
  Tooltip,
  Tr,
  useToast,
} from '@provenance/design';
import { MasterDataListType, type MasterDataItem } from '@provenance/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Copy, Database, Globe, Info, Pencil, Plus, RotateCcw } from 'lucide-react';
import { useState } from 'react';
import { ApiError, api } from '../lib/api';

/**
 * Master data (spec §13).
 *
 * The lists are presented together because they share one shape and one rule, and that
 * rule is the point of the screen: DEACTIVATE, NEVER DELETE. A rejection recorded in
 * 2024 cites a reason by id; hard-deleting that reason turns three years of history into
 * dangling references and makes rejection-reason reporting silently wrong.
 *
 * Platform-global items are shown but not editable here. A chamber that wants its own
 * version of a global item creates one with the same code, and the read path resolves the
 * chamber's in preference — so the override is additive rather than a fork.
 */

const LISTS: Array<{ type: MasterDataListType; label: string; description: string }> = [
  {
    type: MasterDataListType.REJECTION_REASON,
    label: 'Rejection reasons',
    description:
      'What the Reject dialog offers. Structured rather than free text, so a chamber can answer "what are we rejecting most, and why" over a year.',
  },
  {
    type: MasterDataListType.CLARIFICATION_ITEM,
    label: 'Information requests',
    description:
      'One list, two workflows: asking an exporter for something before deciding on a certificate, and asking a registration applicant for something before approving them.',
  },
  {
    type: MasterDataListType.SUPPORTING_DOCUMENT_CATEGORY,
    label: 'Attachment categories',
    description:
      'What an integrator may send as a supporting document category. An unknown code is accepted and filed as Other rather than rejecting the whole submission.',
  },
  {
    type: MasterDataListType.MODIFICATION_REQUEST_TYPE,
    label: 'Modification types',
    description: 'What an exporter can ask to have corrected on an already-issued certificate.',
  },
  {
    type: MasterDataListType.REGISTRATION_REJECTION_REASON,
    label: 'Registration rejections',
    description: 'Why a company registration was not approved.',
  },
];

export function MasterData() {
  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="Master data"
        lede="Managed lists instead of free text, so decisions stay consistent between operators and reportable over time."
      />

      <Card className="flex items-start gap-3">
        <Info aria-hidden className="mt-0.5 size-4 shrink-0 text-[var(--pv-info)]" />
        <p className="[font-size:13px] leading-relaxed text-[var(--pv-ink-soft)]">
          Items are <strong>deactivated, never deleted</strong>. A rejection from two years ago that
          cites a reason has to keep reading correctly — deactivating simply removes the item from
          the picker while leaving history intact. Items marked{' '}
          <Badge tone="neutral" size="sm">
            <Globe aria-hidden className="size-2.5" /> Platform
          </Badge>{' '}
          are shared across every chamber and are maintained centrally.
        </p>
      </Card>

      <Tabs defaultValue={LISTS[0]!.type}>
        <TabsList className="flex-wrap">
          {LISTS.map((list) => (
            <TabsTrigger key={list.type} value={list.type}>
              {list.label}
            </TabsTrigger>
          ))}
        </TabsList>
        {LISTS.map((list) => (
          <TabsContent key={list.type} value={list.type} className="pt-4">
            <MasterDataList listType={list.type} label={list.label} description={list.description} />
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}

function MasterDataList({
  listType,
  label,
  description,
}: {
  listType: MasterDataListType;
  label: string;
  description: string;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<Editing | null>(null);

  const query = useQuery({
    queryKey: ['master-data', listType, 'all'],
    queryFn: () => api.get<MasterDataItem[]>(`/api/console/master-data/${listType}`, { includeInactive: true }),
  });

  const deactivate = useMutation({
    mutationFn: (id: string) => api.del(`/api/console/master-data/${id}`),
    onSuccess: () => {
      toast.success('Deactivated', 'It no longer appears in the picker. History is unchanged.');
      void queryClient.invalidateQueries({ queryKey: ['master-data'] });
    },
    onError: () => toast.error('Could not deactivate that item'),
  });

  const reactivate = useMutation({
    mutationFn: (id: string) => api.patch(`/api/console/master-data/${id}`, { active: true }),
    onSuccess: () => {
      toast.success('Reactivated', 'It appears in the picker again.');
      void queryClient.invalidateQueries({ queryKey: ['master-data'] });
    },
    onError: () => toast.error('Could not reactivate that item'),
  });

  if (query.isLoading) return <Skeleton className="h-64" />;

  const items = query.data ?? [];
  if (items.length === 0) {
    return (
      <>
        <EmptyState
          icon={<Database className="size-5" />}
          title={`No ${label.toLowerCase()} yet`}
          description="Add the first one, or inherit the platform's starter list."
          action={
            <Button
              variant="primary"
              leadingIcon={<Plus className="size-3.5" />}
              onClick={() => setEditing({ mode: 'create' })}
            >
              Add an item
            </Button>
          }
        />
        {editing && (
          <ItemDialog
            listType={listType}
            label={label}
            editing={editing}
            onClose={() => setEditing(null)}
            onSaved={() => {
              setEditing(null);
              void queryClient.invalidateQueries({ queryKey: ['master-data'] });
            }}
          />
        )}
      </>
    );
  }

  return (
    <Card padded={false}>
      <div className="px-5 pt-5">
        <CardHeader
          title={label}
          description={description}
          actions={
            <Button
              variant="secondary"
              size="sm"
              leadingIcon={<Plus className="size-3.5" />}
              onClick={() => setEditing({ mode: 'create' })}
            >
              Add an item
            </Button>
          }
        />
      </div>
      <div className="border-t border-[var(--pv-line)]">
        <TableShell className="rounded-none border-0">
          <Table className="min-w-[720px]">
            <Thead>
              <Tr>
                <Th>Label</Th>
                <Th>Code</Th>
                <Th>Category</Th>
                <Th numeric>Order</Th>
                <Th>Owner</Th>
                <Th className="w-28" />
              </Tr>
            </Thead>
            <Tbody>
              {items.map((item) => (
                <Tr key={item.id} className={item.active ? undefined : 'opacity-55'}>
                  <Td>
                    <span className="font-medium">{item.label}</span>
                    {!item.active && (
                      <Badge tone="neutral" size="sm" className="ml-2">
                        Inactive
                      </Badge>
                    )}
                    {item.description && (
                      <span className="mt-0.5 block [font-size:var(--pv-text-2xs)] text-[var(--pv-faint)]">
                        {item.description}
                      </span>
                    )}
                  </Td>
                  <Td>
                    <CodeChip>{item.code}</CodeChip>
                  </Td>
                  <Td>{item.category ?? '—'}</Td>
                  <Td numeric>{item.sortOrder}</Td>
                  <Td>
                    {item.chamberId === null ? (
                      <Tooltip content="Maintained by the platform and shared by every chamber. Create an item with the same code to override it for your chamber.">
                        <Badge tone="neutral" size="sm">
                          <Globe aria-hidden className="size-2.5" /> Platform
                        </Badge>
                      </Tooltip>
                    ) : (
                      <Badge tone="leaf" size="sm">
                        This chamber
                      </Badge>
                    )}
                  </Td>
                  <Td>
                    <div className="flex flex-wrap justify-end gap-1">
                      {item.chamberId === null ? (
                        // A platform item cannot be edited here. Overriding it means creating
                        // a chamber item with the same code, which the read path prefers — so
                        // the override is additive rather than a fork of the platform list.
                        <Tooltip content="Create your chamber's own version of this item, with the same code">
                          <Button
                            variant="ghost"
                            size="sm"
                            leadingIcon={<Copy className="size-3.5" />}
                            onClick={() =>
                              setEditing({
                                mode: 'create',
                                prefill: {
                                  code: item.code,
                                  label: item.label,
                                  category: item.category ?? '',
                                  description: item.description ?? '',
                                  sortOrder: item.sortOrder,
                                },
                              })
                            }
                          >
                            Override
                          </Button>
                        </Tooltip>
                      ) : (
                        <>
                          <Button
                            variant="ghost"
                            size="sm"
                            leadingIcon={<Pencil className="size-3.5" />}
                            onClick={() => setEditing({ mode: 'edit', item })}
                          >
                            Edit
                          </Button>
                          {item.active ? (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => deactivate.mutate(item.id)}
                              loading={deactivate.isPending}
                            >
                              Deactivate
                            </Button>
                          ) : (
                            <Button
                              variant="ghost"
                              size="sm"
                              leadingIcon={<RotateCcw className="size-3.5" />}
                              onClick={() => reactivate.mutate(item.id)}
                              loading={reactivate.isPending}
                            >
                              Reactivate
                            </Button>
                          )}
                        </>
                      )}
                    </div>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </TableShell>
      </div>

      {editing && (
        <ItemDialog
          listType={listType}
          label={label}
          editing={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void queryClient.invalidateQueries({ queryKey: ['master-data'] });
          }}
        />
      )}
    </Card>
  );
}

/** What the dialog is doing: adding a new item, or editing one this chamber owns. */
type Editing =
  | { mode: 'create'; prefill?: { code: string; label: string; category: string; description: string; sortOrder: number } }
  | { mode: 'edit'; item: MasterDataItem };

/**
 * Add or edit one item.
 *
 * Two rules the form has to enforce, both of them about history rather than convenience:
 *
 *  • **The code is immutable once created.** Documents and registrations cite an item by id,
 *    but reports, integrator payloads and the seed all cite it by CODE. Letting someone
 *    rename `INCOMPLETE` to `MISSING_INFO` would silently rewrite what three years of
 *    rejections appear to say. The API omits it from the update for the same reason.
 *
 *  • **There is no delete.** Deactivation is the strongest thing on offer, and the copy
 *    says so — an admin hunting for a delete button should find the answer rather than
 *    assume it is hidden.
 */
function ItemDialog({
  listType,
  label,
  editing,
  onClose,
  onSaved,
}: {
  listType: MasterDataListType;
  label: string;
  editing: Editing;
  onClose: () => void;
  onSaved: () => void;
}) {
  const toast = useToast();
  const existing = editing.mode === 'edit' ? editing.item : null;
  const prefill = editing.mode === 'create' ? editing.prefill : undefined;

  const [code, setCode] = useState(existing?.code ?? prefill?.code ?? '');
  const [itemLabel, setItemLabel] = useState(existing?.label ?? prefill?.label ?? '');
  const [category, setCategory] = useState(existing?.category ?? prefill?.category ?? '');
  const [description, setDescription] = useState(existing?.description ?? prefill?.description ?? '');
  const [sortOrder, setSortOrder] = useState(String(existing?.sortOrder ?? prefill?.sortOrder ?? 100));
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () => {
      const body = {
        label: itemLabel.trim(),
        category: category.trim() || null,
        description: description.trim() || null,
        sortOrder: Number(sortOrder) || 100,
      };
      return existing
        ? api.patch(`/api/console/master-data/${existing.id}`, body)
        : api.post('/api/console/master-data', { ...body, listType, code: code.trim().toUpperCase() });
    },
    onSuccess: () => {
      toast.success(existing ? 'Saved' : 'Added', existing ? undefined : 'It appears in the picker now.');
      onSaved();
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : 'Something went wrong.'),
  });

  const codeValid = /^[A-Z0-9_]{2,60}$/.test(code.trim().toUpperCase());

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader
          title={existing ? `Edit ${existing.label}` : `Add to ${label.toLowerCase()}`}
          description={
            existing
              ? 'The code cannot change — reports and integrations cite it, and renaming it would rewrite what past records appear to say.'
              : prefill
                ? "Your chamber's own version of a platform item. Same code, so it replaces it in every picker without affecting other chambers."
                : 'Codes are uppercase with underscores, and cannot be changed afterwards.'
          }
        />
        <DialogBody>
          <div className="flex flex-col gap-4">
            <Field
              label="Code"
              required
              help={existing ? 'Fixed once created.' : 'Uppercase letters, numbers and underscores. For example COST_STATEMENT.'}
              error={code && !codeValid ? 'Use uppercase letters, numbers and underscores, at least two characters.' : undefined}
            >
              <Input
                value={code}
                disabled={Boolean(existing)}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                placeholder="COST_STATEMENT"
              />
            </Field>

            <Field label="Label" required help="What an operator reads in the picker.">
              <Input value={itemLabel} onChange={(e) => setItemLabel(e.target.value)} />
            </Field>

            <div className="grid gap-4 sm:grid-cols-[1fr_140px]">
              <Field label="Group" help="Optional. Groups related items together in the picker.">
                <Input value={category} onChange={(e) => setCategory(e.target.value)} placeholder="Company evidence" />
              </Field>
              <Field label="Order" help="Lower first.">
                <Input
                  type="number"
                  min={0}
                  max={9999}
                  value={sortOrder}
                  onChange={(e) => setSortOrder(e.target.value)}
                />
              </Field>
            </div>

            <Field label="Description" help="Optional. Shown as a hint where there is room for one.">
              <Textarea rows={2} value={description} onChange={(e) => setDescription(e.target.value)} />
            </Field>

            {error && (
              <p role="alert" className="[font-size:13px] text-[var(--pv-stop-ink,#8B1D1D)]">
                {error}
              </p>
            )}
          </div>
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={save.isPending}
            disabled={!itemLabel.trim() || (!existing && !codeValid)}
            onClick={() => {
              setError(null);
              save.mutate();
            }}
          >
            {existing ? 'Save' : 'Add'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
